import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { format, parseISO } from 'date-fns';
import { ENQUIRIES_BASE_PATH } from '@features/enquiries/enquiry-navigation';
import { costBreakdownTotalIncTax } from '@shared/utils';
import { BookingForm, BookingFormStatus } from '../interfaces/booking-form.interface';
import { BOOKING_FORM_STATUS_STRIP, STATUS_PILL_CLASS } from '../booking-forms-view/booking-form-status-strip.config';

/**
 * A single booking-form row, as shown in Documents → Bookings and on the Booking and Payment page.
 * Owns the display mapping (title with date, customer/reference meta, status phrase, pill, total)
 * so both places render an identical tile from a raw {@link BookingForm}.
 */
@Component({
  selector: 'booking-tile',
  templateUrl: './booking-tile.component.html',
  standalone: true,
  imports: [CommonModule, CurrencyPipe, RouterLink],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class BookingTileComponent {
  readonly bookingForm = input.required<BookingForm>();

  /** Event date preferred, falling back to created so a date always shows; appended to the title. */
  get title(): string {
    const bf = this.bookingForm();
    const dateSource = bf.eventDate ?? bf.created;
    const dateLabel = dateSource ? format(parseISO(dateSource), 'd MMM yyyy') : '';
    return dateLabel ? `${bf.title} · ${dateLabel}` : bf.title;
  }

  /** Customer name + reference (a Draft has no reference yet). */
  get metaLead(): string {
    const bf = this.bookingForm();
    const reference = bf.status === 'Draft' ? null : bf.reference;
    return [bf.customerName, reference].filter(Boolean).join(' · ');
  }

  /** Status detail (e.g. "Issued · awaiting signature", "Signed 3rd June 2026"). */
  get statusPhrase(): string {
    const bf = this.bookingForm();
    switch (bf.status) {
      case 'Draft':
        return 'Draft · not yet sent';
      case 'Issued':
        return 'Issued · awaiting signature';
      case 'Signed':
        // `updated` stands in for the signed date until the signing feature persists a dedicated one.
        return bf.updated ? `Signed ${format(parseISO(bf.updated), 'do MMMM yyyy')}` : 'Signed';
      case 'Cancelled':
        return bf.cancelledDate ? `Cancelled ${format(parseISO(bf.cancelledDate), 'do MMMM yyyy')}` : 'Cancelled';
    }
  }

  get status(): BookingFormStatus {
    return this.bookingForm().status;
  }

  get pillClass(): string {
    return STATUS_PILL_CLASS[BOOKING_FORM_STATUS_STRIP[this.bookingForm().status].pillTone];
  }

  /**
   * Headline total in pounds, VAT-inclusive — mirrors the invoice tile so a booking form and an
   * invoice built from the same figures show the same amount. Derived from `costsBreakdownJson`
   * (which carries the VAT decision); falls back to the entity's exc-VAT `costsTotal` (pence) when
   * no breakdown is present.
   */
  get amount(): number {
    const bf = this.bookingForm();
    if (bf.costsBreakdownJson) return costBreakdownTotalIncTax(bf.costsBreakdownJson);
    return (bf.costsTotal ?? 0) / 100;
  }

  get routerLink(): string[] {
    const bf = this.bookingForm();
    return [ENQUIRIES_BASE_PATH, 'enquiry', bf.enquiryUuid, 'booking-form', bf.uuid];
  }
}
